import { useMemo } from "react"
import { useTab } from "lib/@components/tabs"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { REGIME_ASSETS_TAB } from "routes/asset-register/plugins/regime-display/regime-display.runtime"
import { getSharingLink } from "routes/sharing/controller/get-sharing-link"
import { useShareLinkCommentRegimeId } from "routes/schedule/plugins/share-link-comments/use-share-link-comment-regime-id"
import { isEnabled } from "lib/@components/feature"
import { RequestedFieldsPanel } from "./requested-fields-panel"
import { RequestedInformationTabLabel } from "./requested-information-tab-label"
import { FEATURE_FLAG_SHARE_LINK_FIELDS } from "./feature-flag-share-link-fields"

export const REQUESTED_INFORMATION_TAB_ID = "requested-information"

// Adds a recipient "Requested Information" tab to the regime tab bar (after Comments) when the share
// link's creator requested one or more fields. Uses useTab (not module-level registerTab) so the
// requestedFields check — which needs the fetched share link — can gate the tab. Regime id is
// resolved the same way the comments tab resolves it, so responses key to the same regime.
//
// title/content are memoised: useTab re-raises its slot refresh whenever they change identity, and a
// fresh element every render (alongside the equally-mounted Comments registrar) churns the regime tab
// slots hard enough to starve the schedule list's async filter (it never settles -> "No Schedules").
export function RequestedInformationTabRegistrar() {
    const [shareLinkId] = useSearchParam("share", null)
    const shareLink = getSharingLink.useResults(shareLinkId)
    const regimeId = useShareLinkCommentRegimeId()
    // Also require the share-link-fields feature demand (recipients inherit it from the link owner's
    // client auth bundle via ConfigureShareLinkDemands), so the tab closes when the feature/auth bundle
    // is disabled instead of bleeding through the link's persisted requestedFields (FIQ-1668).
    const canShow = !!shareLinkId && !!shareLink?.requestedFields?.length && isEnabled(FEATURE_FLAG_SHARE_LINK_FIELDS)

    const title = useMemo(
        () => (canShow ? <RequestedInformationTabLabel shareLinkId={shareLinkId} regimeId={regimeId} /> : false),
        [canShow, shareLinkId, regimeId]
    )
    const content = useMemo(
        () => (canShow ? <RequestedFieldsPanel regimeId={regimeId} priority={160} /> : null),
        [canShow, regimeId]
    )

    useTab({ tab: REGIME_ASSETS_TAB, id: REQUESTED_INFORMATION_TAB_ID, title, content })

    return null
}
